import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Button from "../../../../../components/base/button/button";
import Api from "../../../../../../core/services/api/api";
import {formatPhoneNumber} from "../../../../../../core/services/utils/utils";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import IconButton from "../../../../../components/base/button/icon_button";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {CustomersListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {Add, ImportExportOutlined, OpenInNew} from "@mui/icons-material";
import PartnerDataGrid, {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../containers/data-grid";
import SearchInput from "../../../../../components/base/input/search_input";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {Link, useHistory} from "react-router-dom";
import {Box, Typography} from "@mui/material";
import CreateCustomerDialog from "../../../../../components/dialogs/create-customer";
import ImportCustomersDialog from "../../../../../components/dialogs/import-customers";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import ImportExternalCustomerDialog from "../../../../../components/dialogs/import-external-customer";
import generateName from "../../../../../../core/services/utils/name-generator";
import EnvService from "../../../../../../core/services/env";

const dataGridSaveConfig = {
    name: 'CustomersListViewDataGrid',
    version: 2,
}

const exportingChunkSize = 100;

const initialNewCustomerDialog = {open: false};
const initialImportExternalCustomerDialog = {open: false};
const initialImportCustomersDialog = {open: false, onSuccess: () => void 0, onClose: () => void 0};

const CustomersListView = () => {
    const history = useHistory()
    const operatorAccessLevels = useAccessLevels();
    const [newCustomerDialog, setNewCustomerDialog] = useState(initialNewCustomerDialog);
    const [importExternalCustomerDialog, setImportCustomerDialogState] = useState(initialImportExternalCustomerDialog);
    const [importCustomersDialog, setImportCustomersDialog] = useState(initialImportCustomersDialog);
    const [customers, setCustomers] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('customers-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.customersList);
        configureBehavioralTool((tags) => tags.customersList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: CustomersListDataGridCellKeys.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 0,
                sortable: false,
                resizable: false,
                pinned: true,
            },
            {
                title: "Name",
                name: CustomersListDataGridCellKeys.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 180,
                pinned: true,
            },
            {
                title: "Code",
                name: CustomersListDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Address",
                name: CustomersListDataGridCellKeys.address,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 200,
            },
            {
                title: "City",
                name: CustomersListDataGridCellKeys.city,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Province/State",
                name: CustomersListDataGridCellKeys.province,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
            },
            {
                title: "Postal/Zip Code",
                name: CustomersListDataGridCellKeys.postalCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
            },
            {
                title: "Phone",
                name: CustomersListDataGridCellKeys.phone,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Searches for customers by calling the api regarding this action
     * if the api call was successful, populates the [customers] in the inner state and updates the pagination info
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            sorts: args.orderBy?.field ? [{
                property: args.orderBy?.field,
                isDesc: args.orderBy?.descending,
            }] : [],
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword ?? null,
        }
        const response = await Api.searchCustomersV2(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {

            setCustomers(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Searches among the entries based on the filters from the user.
     * @type {function(*): void}
     */
    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters(prevState => ({...prevState, keyword: keywords?.trim()}));
    }, [searchData])

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @type {function(*=): DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Customers`;
        return {
            title: exportTitle,
            body: list?.map(item => ({
                'Name': item.name ?? '',
                'Code': item.code ?? '',
                'Address': item.address ?? '',
                'City': item.city ?? '',
                'Province/State': item.province ?? '',
                'Postal/Zip Code': item.postalCode ?? '',
                'Phone Number': item.phoneNumber ? formatPhoneNumber(item.phoneNumber) : "",
            })) ?? [],
        }
    }, [])


    /**
     * Fetches all customers from server and returns the formatted data to be exported.
     * @type {function(): Promise<DataGridExportInfo>}
     */
    const canExportCustomersList = operatorAccessLevels.packages.base.exportCustomersList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Navigates to the single quote after the new customer has been successfully created.
     * @type {function(*, *): void}
     */
    const onCreateCustomer = useCallback(async (customerId) => {
        if (customerId) {
            return history.push(routeFunctions.customer.generalInformation.profile(customerId));
        } else {
            searchData.setLoading(true);
            await search(searchData);
            if (!isMounted())
                return;
            searchData.setLoading(false);
        }
    }, [history, isMounted, search, searchData])

    /**
     * Closes the new customer dialog.
     * @type {function(): void}
     */
    const closeNewCustomerDialog = useCallback(() => setNewCustomerDialog(initialNewCustomerDialog), []);

    /**
     * Navigates to the single quote after the new customer has been successfully created.
     * @type {(function(*): Promise<void|undefined>)}
     */
    const onExtraCustomerImported = useCallback(async (customer) => {
        if (customer?.id) {
            return history.push(routeFunctions.customer.generalInformation.profile(customer?.id));
        } else if (!!customer) {
            searchData.setLoading(true);
            await search(searchData);
            if (!isMounted())
                return;
            searchData.setLoading(false);
        }
    }, [history, isMounted, search, searchData])

    /**
     * Opens the import customers dialog.
     * @type {function(): void}
     */
    const openImportCustomersDialog = useCallback(() => setImportCustomersDialog({
        open: true,
        onSuccess: async () => {
            searchData.setLoading(true);
            await search(searchData);
            if (!isMounted())
                return;
            searchData.setLoading(false);
        },
        onClose: () => setImportCustomersDialog(initialImportCustomersDialog),
    }), [isMounted, search, searchData])

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return customers?.map(customer => ({
            key: customer.id,
            data: customer,
            cells: {
                [CustomersListDataGridCellKeys.navigate]: operatorAccessLevels.packages.base.customerDetails &&
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.customer.base(customer.id)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [CustomersListDataGridCellKeys.name]: EnvService.enableDemoNameMasking ? generateName(customer.name): customer.name,
                [CustomersListDataGridCellKeys.code]: customer.code,
                [CustomersListDataGridCellKeys.address]: customer.address,
                [CustomersListDataGridCellKeys.city]: customer?.city,
                [CustomersListDataGridCellKeys.province]: customer.province,
                [CustomersListDataGridCellKeys.postalCode]: customer.postalCode,
                [CustomersListDataGridCellKeys.phone]: customer.phoneNumber ? formatPhoneNumber(customer.phoneNumber) : "",
            }
        })) ?? []
    }, [customers, operatorAccessLevels.packages.base.customerDetails])

    return (
        <>
            <div className='d-flex w-100 justify-content-between align-items-center mt-3 mb-4'>
                <Typography variant={'h6'} fontWeight={600}>
                    Customers List
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                    {
                        operatorAccessLevels.packages.base.createCustomer &&
                        <Button
                            className='d-flex align-items-center justify-content-center'
                            variant={'outlined'}
                            color={'primary'}
                            disabled={searchData.loading}
                            onClick={openImportCustomersDialog}
                        >
                            Import customers
                        </Button>
                    }
                    {
                        operatorAccessLevels.packages.base.importExternalCustomer &&
                        <Button
                            className='d-flex align-items-center justify-content-center'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => setImportCustomerDialogState({open: true})}
                        >
                            <ImportExportOutlined className={'mr-2'}/>
                            <p>Import External Customer</p>
                        </Button>
                    }
                    {
                        operatorAccessLevels.packages.base.createCustomer &&
                        <Button
                            className='d-flex align-items-center justify-content-center'
                            variant={'outlined'}
                            color={'primary'}
                            disabled={searchData.loading}
                            onClick={() => setNewCustomerDialog({open: true})}
                        >
                            <Add className={`icon small`} style={{margin: '2px 0'}}/>
                            <p className='ml-2'>Add new</p>
                        </Button>
                    }
                </Box>
            </div>
            <SearchInput
                loading={searchData.loading}
                autoSearch
                immediateSearch
                search={searchByKeyword}
                initialValue={searchData.filters?.keyword ?? undefined}
                label={'Search'}
            />
            <div className='w-100 my-4'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns: columns}}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportCustomersList ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
            <CreateCustomerDialog
                open={newCustomerDialog?.open}
                close={closeNewCustomerDialog}
                onSuccess={onCreateCustomer}
            />
            <ImportCustomersDialog
                open={importCustomersDialog?.open}
                close={importCustomersDialog?.onClose}
                onSuccess={importCustomersDialog?.onSuccess}
            />
            {
                operatorAccessLevels.packages.base.importExternalCustomer &&
                <ImportExternalCustomerDialog
                    open={importExternalCustomerDialog.open}
                    setOpen={() => setImportCustomerDialogState(initialImportExternalCustomerDialog)}
                    onSuccess={onExtraCustomerImported}
                />
            }
        </>
    );
}

export default CustomersListView;
